import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useState } from 'react'

import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   Button,
   Hierarchy,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import {
   EyeIcon,
   EyeOffOutlineIcon,
   FeaturedPasscodeLockIcon
} from '@flowwai-frontend/design-system/icons'
import { UserPINUpdateMode } from '@flowwai-frontend/shared-web/iam/ui'
import { APIStatus, isAPIFetching } from '@flowwai-frontend/shared/api-common'
import { useCustomT } from '@flowwai-frontend/shared/common-components'

import * as Styles from './styles'

interface Props {
   mode: UserPINUpdateMode
   pin: string
   pinErrorMessage: string | null
   confirmPin: string
   confirmPinErrorMessage: string | null
   apiStatus: APIStatus
   isPINValid: boolean
   onChangePin: (pin: string) => void
   onChangeConfirmPin: (confirmPin: string) => void
   onSubmit: () => void
   onCancel: () => void
}

const UserPINInput = (props: Props): React.ReactElement => {
   const {
      mode,
      pin,
      pinErrorMessage,
      confirmPin,
      confirmPinErrorMessage,
      apiStatus,
      isPINValid,
      onChangePin,
      onChangeConfirmPin,
      onSubmit,
      onCancel
   } = props

   const [showPassword, setShowPassword] = useState(false)

   const { theme } = useAppTheme()
   const themes = theme.iam.userPIN
   const t = useCustomT('iam:userPIN')

   const isResetPIN = mode === 'RESET_PIN'

   const renderShowPasswordToggle = () => (
      <button onClick={() => setShowPassword(!showPassword)}>
         {showPassword ? <EyeOffOutlineIcon /> : <EyeIcon />}
      </button>
   )

   const renderPin = () => {
      return (
         <TextField
            autoFocus
            errorMessage={pinErrorMessage}
            label={t('enterNewPINLabel')}
            placeholder={t('enterPINPlaceholder')}
            size={'ExtraSmall'}
            value={pin}
            type={showPassword ? 'text' : 'password'}
            onBlur={() => onChangePin(pin)}
            onChange={onChangePin}
            rightElement={renderShowPasswordToggle}
         />
      )
   }
   const renderConfirmPin = () => {
      return (
         <TextField
            errorMessage={confirmPinErrorMessage}
            label={t('confirmNewPINLabel')}
            placeholder={t('enterPINPlaceholder')}
            size={'ExtraSmall'}
            value={confirmPin}
            type='password'
            onBlur={() => onChangeConfirmPin(confirmPin)}
            onChange={onChangeConfirmPin}
         />
      )
   }

   const renderSubmitButton = () => (
      <Button
         hierarchy={Hierarchy.Primary}
         size={'Large'}
         subVariant={SubVariant.Primary}
         isLoading={isAPIFetching(apiStatus)}
         onClick={onSubmit}
         className='flex-1'
         isDisabled={!isPINValid}
      >
         {isResetPIN ? t('resetPIN') : t('setPIN')}
      </Button>
   )

   const renderCancelButton = () => (
      <Button
         hierarchy={Hierarchy.Secondary}
         size={'Large'}
         subVariant={SubVariant.GrayOutline}
         onClick={onCancel}
         className='flex-1'
      >
         {t('cancel')}
      </Button>
   )

   const renderHeader = () => (
      <div className={cn(Styles.headerStyles, themes.containerBorderColor)}>
         <FeaturedPasscodeLockIcon height={20} width={20} />
         <p className={cn(Styles.headerTitleStyles, themes.headerTitleColor)}>
            {t('title')}
         </p>
      </div>
   )

   return (
      <div className={Styles.containerStyles}>
         {renderHeader()}
         <div className={Styles.bodyStyles}>
            {renderPin()}
            {renderConfirmPin()}
         </div>
         <div className={cn(Styles.footerStyles, themes.containerBorderColor)}>
            {renderCancelButton()}
            {renderSubmitButton()}
         </div>
      </div>
   )
}

export default observer(UserPINInput)
